import { sortSectionsForSnapshotDisplay } from "./sectionDisplayOrdinal";

/** Avoid the browser reusing a cached response for a different snapshot URL (Original vs New). */
export const SNAPSHOT_FETCH_INIT = { cache: "no-store" };

export function isTruthyMainEntrySnapshot(value) {
  return (
    value === true ||
    (typeof value === "string" && value.trim().toLowerCase() === "true")
  );
}

/**
 * Copy-replace snapshot: root `first_entry` may include nested `first_entry: true` and both
 * `originalContent` (View Original) and `newContent` (View New) in that same object.
 */
export function isSnapshotRootFirstEntryDualEnvelope(firstEntry) {
  if (!firstEntry || typeof firstEntry !== "object" || Array.isArray(firstEntry)) {
    return false;
  }
  return isTruthyMainEntrySnapshot(firstEntry.first_entry);
}

/**
 * Bulk copy/move snapshot (section, step, substep + child chain): top-level UUID keys each hold
 * `{ title, originalContent, newContent }`, plus optional root `version_content` (metadata only; not a block).
 * When audit `version_content.main_entry` is true:
 * - If root `first_entry` has nested `first_entry: true`, View Original / New use that block's
 *   `originalContent` / `newContent` (copy-replace dual envelope).
 * - Else View Original uses root `first_entry.originalContent` when non-empty, else first op's
 *   `originalContent`; View New uses the last UUID operation block's `newContent`.
 * Same rules on `/audit-trail/reviewcopymovedelete/…` via `options.bulkReviewFirstOriginalLastNew`.
 */
export const isUuidKeyedBulkBlock = (val) => {
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false;
  return Array.isArray(val.originalContent) || Array.isArray(val.newContent);
};

/** Ordered UUID keys for bulk copy/move/delete snapshot blocks (excludes root `version_content`). */
export function listBulkSnapshotBlockKeys(data) {
  if (typeof data !== 'object' || data == null || Array.isArray(data)) return [];
  return Object.keys(data).filter(
    (k) => k !== 'version_content' && isUuidKeyedBulkBlock(data[k]),
  );
}

/**
 * Full procedure tree from revision / audit-trail S3 (`{ data: sections[], part_number, operation?, … }`)
 * with no UUID-keyed `{ originalContent, newContent }` blocks. For these, View Original / New must use
 * `data` only — never {@link tryPreviewTreeBulkSnapshot} (e.g. UPDATE_STEP + item-code save snapshots).
 */
function isProcedureTreeDataOnlyEnvelope(data) {
  return (
    typeof data === 'object' &&
    data != null &&
    !Array.isArray(data) &&
    Array.isArray(data.data) &&
    listBulkSnapshotBlockKeys(data).length === 0
  );
}

/**
 * Procedure type for headers: prefer `item_code_type` / `type_name`; ignore root `type` when it mirrors
 * audit actions (UPDATE_PART, …).
 */
export function snapshotEnvelopeItemCodeType(source) {
  if (source == null || typeof source !== 'object' || Array.isArray(source)) {
    return undefined;
  }
  for (const key of ['item_code_type', 'type_name']) {
    const v = source[key];
    if (v == null) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
      continue;
    }
    const s = String(v).trim();
    if (s) return s;
  }
  const t = source.type;
  if (typeof t !== 'string' || !t.trim()) return undefined;
  const trimmed = t.trim();
  if (
    /^(UPDATE|CREATE|DELETE|SAVE|EDIT|COPY|MOVE|REBASE|RESTORE)_[A-Z][A-Z0-9_]*$/i.test(
      trimmed,
    )
  ) {
    return undefined;
  }
  return trimmed;
}

/**
 * Operation blocks only: excludes root `first_entry` (baseline tree for copy-replace) so
 * "last newContent" is the final UUID op, not misplaced when key order differs.
 */
export function listMainEntryBulkOperationBlockKeys(data) {
  return listBulkSnapshotBlockKeys(data).filter((k) => k !== 'first_entry');
}

/**
 * Separates root snapshot metadata from UUID-keyed `{ title, originalContent, newContent }` blocks
 * (bulk copy/move S3 JSON: `{ ...blocks, version_content }`).
 */
export function peelBulkSnapshotRoot(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { versionContent: null, blocksById: data };
  }
  const versionContent =
    data.version_content &&
    typeof data.version_content === 'object' &&
    !Array.isArray(data.version_content)
      ? data.version_content
      : null;
  const blocksById = { ...data };
  delete blocksById.version_content;
  return { versionContent, blocksById };
}

/** Merge one `version_content` nesting level (bulk_audit_trail root envelope). */
const flattenSnapshotRootVc = (rootVc) => {
  if (!rootVc || typeof rootVc !== 'object' || Array.isArray(rootVc)) return rootVc;
  const inner = rootVc.version_content;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...rootVc, ...inner };
  }
  return rootVc;
};

const metaFromBulkSnapshotVersionContent = (rootVc) => {
  if (!rootVc || typeof rootVc !== 'object' || Array.isArray(rootVc)) return null;
  const v = flattenSnapshotRootVc(rootVc);
  return {
    partNumber: v.partNumber ?? v.part_number ?? v.procedure_number,
    revision:
      v.revision ?? v.rev ?? v.part_rev ?? v.to_part_rev ?? v.from_part_rev,
    status:
      v.status ??
      v.current_status ??
      v.status_changed_to ??
      v.prev_revision_status,
    item_code_type: snapshotEnvelopeItemCodeType(v),
    partDescription: v.partDescription,
    operation: v.operation,
    action_to_show: v.action_to_show,
    tree_node_id:
      v.tree_node_id ||
      v.procedure_tree_ref ||
      v.section_tree_node_id,
    pageTitle: v.pageTitle,
    breadcrumb_text: v.breadcrumb_text,
    section_name: v.section_name,
  };
};

/**
 * Audit `version_content.first_entry === true` (boolean/string) — prefer root snapshot `first_entry.originalContent` /
 * `first_entry.newContent` for View Original / View New ({@link tryMainEntryBulkSnapshot}); not the nested S3 shape alone.
 */
function pickAuditFirstEntryFlagFromVc(vcMerged) {
  if (!vcMerged || typeof vcMerged !== 'object') return false;
  const fe = vcMerged.first_entry;
  if (fe === true) return true;
  if (typeof fe === 'string' && fe.trim().toLowerCase() === 'true') return true;
  return false;
}

const tryMainEntryBulkSnapshot = (data, viewType, options = {}) => {
  if (typeof data !== 'object' || data == null || Array.isArray(data)) {
    return null;
  }
  /** Separated from UUID-keyed operation blocks (part/rev/status, action_to_show, …). */
  const snapshotVersionContent =
    data.version_content &&
    typeof data.version_content === 'object' &&
    !Array.isArray(data.version_content)
      ? data.version_content
      : null;

  const operationKeys = listMainEntryBulkOperationBlockKeys(data);
  const firstEntry =
    data.first_entry &&
    typeof data.first_entry === 'object' &&
    !Array.isArray(data.first_entry)
      ? data.first_entry
      : null;

  const firstEntryDual = isSnapshotRootFirstEntryDualEnvelope(firstEntry);
  const copyReplace = options.copyReplaceBulk === true;
  const auditFirstEntryFlag = options.auditFirstEntryFlag === true;
  const mainEntryBulk = options.mainEntryBulk === true;
  const revisionCopiedMainEntry = options.revisionCopiedMainEntryBulk === true;
  const auditVcFirstEntryFlagOpt = options.auditVcFirstEntryFlag === true;

  /** Copy-replace COND1 — `main_entry` in preview and not VC `first_entry` → `first_entry.originalContent` + last UUID `newContent`. */
  const copyReplaceCond1 =
    copyReplace && mainEntryBulk && !auditFirstEntryFlag;
  /**
   * Audit row VC: REVISION_COPIED + main_entry (not copy-replace text match). Same S3 shape as COND1.
   */
  const revisionCopiedCond1 =
    revisionCopiedMainEntry && mainEntryBulk && !auditVcFirstEntryFlagOpt;

  let rawRows = null;

  /**
   * Audit VC `first_entry: true` — root snapshot `first_entry.originalContent` / `first_entry.newContent`
   * (copy-replace COND2 and same shape for REVISION_COPIED / bulk COPEID_REPLACE when VC sends the flag).
   */
  if (auditVcFirstEntryFlagOpt) {
    if (!firstEntry) return null;
    rawRows =
      viewType === 'original'
        ? firstEntry.originalContent
        : firstEntry.newContent;
    if (!Array.isArray(rawRows)) rawRows = [];
  } else if (copyReplaceCond1 || revisionCopiedCond1) {
    if (viewType === 'original') {
      if (firstEntry && Array.isArray(firstEntry.originalContent)) {
        rawRows = firstEntry.originalContent;
      } else if (operationKeys.length > 0) {
        rawRows = data[operationKeys[0]]?.originalContent;
      }
      if (!Array.isArray(rawRows)) rawRows = [];
    } else if (operationKeys.length > 0) {
      const lastOp = data[operationKeys[operationKeys.length - 1]];
      rawRows = lastOp?.newContent;
      if (!Array.isArray(rawRows)) rawRows = [];
    } else {
      rawRows = [];
    }
  } else if (copyReplace) {
    return null;
  } else if (firstEntryDual) {
    rawRows =
      viewType === 'original'
        ? firstEntry?.originalContent
        : firstEntry?.newContent;
    if (!Array.isArray(rawRows)) rawRows = [];
  } else if (viewType === 'original') {
    if (
      firstEntry &&
      Array.isArray(firstEntry.originalContent) &&
      firstEntry.originalContent.length > 0
    ) {
      rawRows = firstEntry.originalContent;
    } else if (operationKeys.length > 0) {
      const firstOp = data[operationKeys[0]];
      rawRows = firstOp?.originalContent;
    }
  } else if (operationKeys.length > 0) {
    const lastOp = data[operationKeys[operationKeys.length - 1]];
    rawRows = lastOp?.newContent;
  }

  if (!Array.isArray(rawRows)) return null;

  return {
    rows: sortSectionsForSnapshotDisplay(rawRows),
    meta: metaFromBulkSnapshotVersionContent(snapshotVersionContent),
  };
};

/** Detail fields that may equal audit preview_tree_node_id inside snapshot rows. */
const PREVIEW_ID_DETAIL_FIELDS = [
  'procedure_tree_ref',
  'step_tree_node_id',
  'section_tree_node_id',
  'sub_step_tree_node_id',
  'rich_text_node_id',
  'attachment_tree_node_id',
  'video_tree_node_id',
  'canvas_tree_node_id',
  'canva_tree_node_id',
];

const nodeMatchesPreviewId = (node, id) => {
  if (!node || !id) return false;
  if (node.tree_node_id === id || node.procedure_tree_ref === id) return true;
  if (
    node.step_id_ref === id ||
    node.section_id_ref === id ||
    node.substep_id_ref === id
  ) {
    return true;
  }
  const d = node.detail;
  if (d && typeof d === 'object') {
    if (
      d.procedure_tree_ref === id ||
      d.step_tree_node_id === id ||
      d.section_tree_node_id === id ||
      d.sub_step_tree_node_id === id
    ) {
      return true;
    }
    for (const f of PREVIEW_ID_DETAIL_FIELDS) {
      if (d[f] === id) return true;
    }
  }
  return false;
};

const walkChildrenForPreview = (children, id) => {
  if (!Array.isArray(children)) return false;
  for (const ch of children) {
    if (nodeMatchesPreviewId(ch, id)) return true;
  }
  return false;
};

const walkSubstepsForPreview = (substeps, id) => {
  if (!Array.isArray(substeps)) return false;
  for (const ss of substeps) {
    if (nodeMatchesPreviewId(ss, id)) return true;
    if (walkChildrenForPreview(ss.children, id)) return true;
  }
  return false;
};

const walkStepsForPreview = (steps, id) => {
  if (!Array.isArray(steps)) return false;
  for (const st of steps) {
    if (nodeMatchesPreviewId(st, id)) return true;
    if (walkSubstepsForPreview(st.substeps, id)) return true;
    if (walkChildrenForPreview(st.children, id)) return true;
  }
  return false;
};

const procedureTreeContainsPreviewId = (rows, id) => {
  if (!Array.isArray(rows) || !id) return false;
  for (const sec of rows) {
    if (nodeMatchesPreviewId(sec, id)) return true;
    if (walkStepsForPreview(sec.steps, id)) return true;
  }
  return false;
};

/**
 * When preview id is not a top-level snapshot key (e.g. source substep id vs op block key),
 * use the first bulk block whose trees reference that id (Copied Substep + follow-on ops).
 */
export function findBulkBlockByPreviewId(data, focusId) {
  const blockKeys = Object.keys(data).filter(
    (k) => k !== 'version_content' && isUuidKeyedBulkBlock(data[k]),
  );
  for (const k of blockKeys) {
    const b = data[k];
    if (
      procedureTreeContainsPreviewId(b.originalContent, focusId) ||
      procedureTreeContainsPreviewId(b.newContent, focusId)
    ) {
      return b;
    }
  }
  return null;
}

/**
 * Non–main_entry bulk: top-level key === audit preview_tree_node_id → that block's
 * originalContent / newContent for View Original / View New.
 * Falls back to the first block whose content trees reference preview_tree_node_id.
 */
const tryPreviewTreeBulkSnapshot = (data, viewType, previewTreeNodeId) => {
  if (typeof data !== 'object' || data == null || Array.isArray(data)) {
    return null;
  }
  const focusId =
    previewTreeNodeId == null ? '' : String(previewTreeNodeId).trim();
  if (!focusId) return null;

  let block = data[focusId];
  if (!isUuidKeyedBulkBlock(block)) {
    block = findBulkBlockByPreviewId(data, focusId);
  }
  if (!isUuidKeyedBulkBlock(block)) return null;

  const rawRows =
    viewType === 'original' ? block.originalContent : block.newContent;
  if (!Array.isArray(rawRows)) return null;

  const rootVc =
    data.version_content && typeof data.version_content === 'object' && !Array.isArray(data.version_content)
      ? data.version_content
      : null;

  return {
    rows: sortSectionsForSnapshotDisplay(rawRows),
    meta: metaFromBulkSnapshotVersionContent(rootVc),
  };
};

/**
 * Parses JSON from S3 or inline API fields into section rows + optional metadata.
 * Handles: { data: [...] } section/save envelopes, arrays, result, original/new, auditData, UUID-keyed merge.
 * @param {unknown} data - Parsed JSON
 * @param {'original'|'new'} viewType - Branch for paired original/new shapes
 * @param {{ mainEntryBulk?: boolean, previewTreeNodeId?: string, childProcedureSnapshots?: boolean, bulkReviewFirstOriginalLastNew?: boolean, copyReplaceBulk?: boolean, auditFirstEntryFlag?: boolean, revisionCopiedMainEntryBulk?: boolean, auditVcFirstEntryFlag?: boolean, auditUpdateStepProcedureTree?: boolean, auditLeafEditProcedureTree?: boolean, preserveSnapshotSectionOrder?: boolean }} [options] - `auditVcFirstEntryFlag`: audit VC `first_entry: true` → root snapshot `first_entry.originalContent` / `newContent`. Copy-replace: `copyReplaceBulk` with `auditFirstEntryFlag` / `main_entry` selects COND1–3; `revisionCopiedMainEntryBulk` + `main_entry` selects REVISION_COPIED main-entry bulk (first + last UUID op) when VC does not use `first_entry`.
 * @returns {{ rows: Array, meta: object|null }}
 */
export function parseSnapshotJson(data, viewType = 'new', options = {}) {
  if (data == null) return { rows: [], meta: null };

  /**
   * Full procedure-tree audits (`UPDATE_STEP`, `UPDATE_CANVAS`, …): use `data.data` (and optional root
   * metadata). Runs before child-move / bulk paths. When `preserveSnapshotSectionOrder` is true, keep API
   * array order so item-cat preview / review AppBar `resolveItemCatPreviewNode` matches.
   */
  if (
    options.auditUpdateStepProcedureTree === true ||
    options.auditLeafEditProcedureTree === true
  ) {
    const preserveOrder = options.preserveSnapshotSectionOrder === true;
    if (
      typeof data === 'object' &&
      !Array.isArray(data) &&
      Array.isArray(data.data)
    ) {
      const rawRows = data.data;
      return {
        rows: preserveOrder
          ? rawRows
          : sortSectionsForSnapshotDisplay(rawRows),
        meta: {
          partNumber:
            data.partNumber ?? data.part_number ?? data.procedure_number,
          revision:
            data.revision ??
            data.rev ??
            data.part_rev ??
            data.procedure_rev ??
            data.to_part_rev ??
            data.from_part_rev,
          status:
            data.status ??
            data.current_status ??
            data.status_changed_to ??
            data.prev_revision_status ??
            data.procedure_status,
          item_code_type: snapshotEnvelopeItemCodeType(data),
          pageTitle: data.pageTitle,
          breadcrumb_text: data.breadcrumb_text,
          tree_node_id:
            data.preview_tree_node_id ||
            data.tree_node_id ||
            data.canvas_tree_node_id ||
            data.canva_tree_node_id ||
            data.procedure_tree_id ||
            data.section_tree_node_id,
          section_name: data.section_name,
          operation: data.operation,
          type: data.type,
          action_to_show: data.action_to_show,
        },
      };
    }
    if (Array.isArray(data)) {
      return {
        rows: preserveOrder ? data : sortSectionsForSnapshotDisplay(data),
        meta: null,
      };
    }
    if (
      typeof data === 'object' &&
      !Array.isArray(data) &&
      data.result &&
      Array.isArray(data.result)
    ) {
      return {
        rows: preserveOrder
          ? data.result
          : sortSectionsForSnapshotDisplay(data.result),
        meta: null,
      };
    }
  }

  /**
   * Child-node move/copy (attachment, video, rich text, canvas/canva, form — e.g. VIDEO_COPIED,
   * RICHTEXT_MOVED): previous snapshot is a plain section array; new snapshot is
   * { data: sections[], operation: 'single_audit_trail', ... }.
   * View Original / View New must come from those payloads only (no bulk merge / preview_tree_node_id).
   */
  if (options.childProcedureSnapshots === true) {
    if (Array.isArray(data)) {
      return { rows: sortSectionsForSnapshotDisplay(data), meta: null };
    }
    if (
      typeof data === 'object' &&
      !Array.isArray(data) &&
      Array.isArray(data.data)
    ) {
      return {
        rows: sortSectionsForSnapshotDisplay(data.data),
        meta: {
          partNumber:
            data.partNumber ?? data.part_number ?? data.procedure_number,
          revision:
            data.revision ??
            data.rev ??
            data.part_rev ??
            data.to_part_rev ??
            data.from_part_rev,
          status:
            data.status ??
            data.current_status ??
            data.status_changed_to ??
            data.prev_revision_status,
          item_code_type: snapshotEnvelopeItemCodeType(data),
          pageTitle: data.pageTitle,
          breadcrumb_text: data.breadcrumb_text,
          tree_node_id:
            data.tree_node_id ||
            data.procedure_tree_id ||
            data.section_tree_node_id,
          section_name: data.section_name,
          operation: data.operation,
          type: data.type,
          action_to_show: data.action_to_show,
        },
      };
    }
  }

  /**
   * `/audit-trail/reviewcopymovedelete/…` only: when `main_entry` is true (audit options or snapshot root
   * `version_content`), use the first UUID block's `originalContent` (View Original) and the last block's
   * `newContent` (View New). When `main_entry` is not set, use only the block for `preview_tree_node_id`
   * (top-level snapshot key or {@link findBulkBlockByPreviewId}) — do not infer chain from `bulk_audit_trail` alone.
   */
  if (options.bulkReviewFirstOriginalLastNew === true) {
    const vcRoot =
      typeof data === 'object' &&
      data != null &&
      !Array.isArray(data) &&
      data.version_content &&
      typeof data.version_content === 'object' &&
      !Array.isArray(data.version_content)
        ? data.version_content
        : null;
    const useMainEntryChain =
      options.mainEntryBulk === true ||
      isTruthyMainEntrySnapshot(vcRoot?.main_entry) ||
      options.auditVcFirstEntryFlag === true ||
      (options.copyReplaceBulk === true && options.auditFirstEntryFlag === true) ||
      options.revisionCopiedMainEntryBulk === true;

    if (useMainEntryChain) {
      const chainParsed = tryMainEntryBulkSnapshot(data, viewType, options);
      if (chainParsed) return chainParsed;
    }
    if (
      options.previewTreeNodeId &&
      !options.auditVcFirstEntryFlag &&
      (!options.copyReplaceBulk ||
        (!options.mainEntryBulk && !options.auditFirstEntryFlag))
    ) {
      const op = String(data.operation || "").trim();
      const looksLikeUuidBulk = listBulkSnapshotBlockKeys(data).length > 0;
      const procedureTreeDataOnly = isProcedureTreeDataOnlyEnvelope(data);
      if (
        !procedureTreeDataOnly &&
        (op !== "single_audit_trail" || looksLikeUuidBulk)
      ) {
        const focused = tryPreviewTreeBulkSnapshot(
          data,
          viewType,
          String(options.previewTreeNodeId),
        );
        if (focused) return focused;
      }
    }
  }

  const shouldTryMainEntryBulk =
    options.childProcedureSnapshots !== true &&
    (options.mainEntryBulk === true ||
      options.auditVcFirstEntryFlag === true ||
      (options.copyReplaceBulk === true && options.auditFirstEntryFlag === true) ||
      options.revisionCopiedMainEntryBulk === true);

  if (shouldTryMainEntryBulk) {
    const mainEntryParsed = tryMainEntryBulkSnapshot(data, viewType, options);
    if (mainEntryParsed) return mainEntryParsed;
  }

  /**
   * UUID-keyed bulk (section/step copy…): prefer the block for this audit row's
   * `preview_tree_node_id` so View Original/New show that op's trees, not first-block / last-block
   * across unrelated operations in the same file (e.g. Section copy op 1 vs Step copy op 2 of 8).
   * If `operation` is `single_audit_trail` but the payload still has UUID blocks, resolve by preview id anyway.
   * Copy & replace COND3: only when not `main_entry` and not preview `first_entry: true`.
   */
  if (
    typeof data === "object" &&
    !Array.isArray(data) &&
    options.childProcedureSnapshots !== true &&
    options.previewTreeNodeId &&
    !options.auditVcFirstEntryFlag &&
    (!options.copyReplaceBulk ||
      (!options.mainEntryBulk && !options.auditFirstEntryFlag))
  ) {
    const op = String(data.operation || "").trim();
    const looksLikeUuidBulk = listBulkSnapshotBlockKeys(data).length > 0;
    const procedureTreeDataOnly = isProcedureTreeDataOnlyEnvelope(data);
    if (
      !procedureTreeDataOnly &&
      (op !== "single_audit_trail" || looksLikeUuidBulk)
    ) {
      const focused = tryPreviewTreeBulkSnapshot(
        data,
        viewType,
        String(options.previewTreeNodeId),
      );
      if (focused) return focused;
    }
  }

  // Section save / audit envelope (e.g. new_content_snapshot from uploadJsonToS3)
  if (
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray(data.data)
  ) {
    return {
      rows: sortSectionsForSnapshotDisplay(data.data),
      meta: {
        partNumber:
          data.partNumber ?? data.part_number ?? data.procedure_number,
        revision:
          data.revision ??
          data.rev ??
          data.part_rev ??
          data.to_part_rev ??
          data.from_part_rev,
        status:
          data.status ??
          data.current_status ??
          data.status_changed_to ??
          data.prev_revision_status,
        item_code_type: snapshotEnvelopeItemCodeType(data),
        pageTitle: data.pageTitle,
        breadcrumb_text: data.breadcrumb_text,
        tree_node_id:
          data.tree_node_id ||
          data.procedure_tree_id ||
          data.section_tree_node_id,
        section_name: data.section_name,
        operation: data.operation,
        type: data.type,
        action_to_show: data.action_to_show,
      },
    };
  }

  if (Array.isArray(data)) {
    return { rows: sortSectionsForSnapshotDisplay(data), meta: null };
  }

  if (data.result && Array.isArray(data.result)) {
    return { rows: sortSectionsForSnapshotDisplay(data.result), meta: null };
  }

  if (data.originalContent && data.newContent) {
    const content =
      viewType === 'original' ? data.originalContent : data.newContent;
    return {
      rows: Array.isArray(content)
        ? sortSectionsForSnapshotDisplay(content)
        : [],
      meta: null,
    };
  }

  if (data.auditData) {
    return parseSnapshotJson(data.auditData, viewType, options);
  }

  /**
   * Several UUID-keyed bulk operations in one snapshot file: each block often carries a *full*
   * procedure `newContent` after that op. Merging by section id then duplicates the whole tree
   * when ids are reissued per block (common when `main_entry` is missing on the audit row).
   * Align with `main_entry` semantics: View Original = first block's `originalContent`,
   * View New = last block's `newContent` (final state only).
   */
  if (
    typeof data === 'object' &&
    data != null &&
    !Array.isArray(data) &&
    !options.previewTreeNodeId &&
    !options.childProcedureSnapshots
  ) {
    const bulkBlockKeys = Object.keys(data).filter(
      (k) => k !== 'version_content' && isUuidKeyedBulkBlock(data[k]),
    );
    if (bulkBlockKeys.length >= 2) {
      const mainStyle = tryMainEntryBulkSnapshot(data, viewType, options);
      if (mainStyle) return mainStyle;
    }
  }

  // UUID-keyed object: merge all keys into full procedure tree
  const keys = Object.keys(data);
  const getSectionId = (s) =>
    s?.tree_node_id || s?.detail?.section_tree_node_id || s?.section_id_ref;
  const getStepId = (st) =>
    st?.tree_node_id || st?.step_id_ref || st?.detail?.step_tree_node_id;
  const getSubstepId = (ss) =>
    ss?.tree_node_id || ss?.substep_id_ref || ss?.detail?.sub_step_tree_node_id;
  const getChildId = (c) =>
    c?.tree_node_id || c?.detail?.element_id || c?.detail?.canvas_tree_node_id || c?.detail?.canva_tree_node_id ||
    c?.detail?.rich_text_node_id || c?.detail?.rich_text_tree_node_id || c?.detail?.video_tree_node_id ||
    c?.detail?.attachment_tree_node_id || c?.detail?.preview_tree_node_id;

  const mergeSubstep = (existing, incoming) => {
    if (!existing) return incoming;
    const childrenMap = new Map();
    (existing.children || []).forEach((ch) => { const id = getChildId(ch); if (id) childrenMap.set(id, ch); });
    (incoming.children || []).forEach((ch) => { const id = getChildId(ch); if (id) childrenMap.set(id, ch); });
    return { ...incoming, children: Array.from(childrenMap.values()) };
  };

  const mergeStep = (existing, incoming) => {
    if (!existing) return incoming;
    const childrenMap = new Map();
    (existing.children || []).forEach((ch) => { const id = getChildId(ch); if (id) childrenMap.set(id, ch); });
    (incoming.children || []).forEach((ch) => { const id = getChildId(ch); if (id) childrenMap.set(id, ch); });
    const substepsMap = new Map();
    (existing.substeps || []).forEach((ss) => { const id = getSubstepId(ss); if (id) substepsMap.set(id, ss); });
    (incoming.substeps || []).forEach((ss) => {
      const id = getSubstepId(ss);
      substepsMap.set(id, mergeSubstep(substepsMap.get(id), ss));
    });
    return { ...incoming, children: Array.from(childrenMap.values()), substeps: Array.from(substepsMap.values()) };
  };

  const sectionsMap = new Map();
  for (const key of keys) {
    const content = data[key];
    if (!content || !(content.originalContent || content.newContent)) continue;
    const arr = viewType === 'original' ? content.originalContent : content.newContent;
    if (!Array.isArray(arr)) continue;
    for (const section of arr) {
      const sid = getSectionId(section);
      if (!sid) continue;
      let sec = sectionsMap.get(sid);
      if (!sec) {
        sec = { ...section, steps: [] };
        sectionsMap.set(sid, sec);
      }
      const stepsMap = new Map();
      (sec.steps || []).forEach((st) => { const stepId = getStepId(st); if (stepId) stepsMap.set(stepId, st); });
      (section.steps || []).forEach((st) => {
        const stepId = getStepId(st);
        if (stepId) stepsMap.set(stepId, mergeStep(stepsMap.get(stepId), st));
      });
      sec.steps = Array.from(stepsMap.values()).sort(
        (a, b) =>
          parseInt(a?.sibling_order ?? a?.detail?.sibling_order ?? '0', 10) -
          parseInt(b?.sibling_order ?? b?.detail?.sibling_order ?? '0', 10),
      );
    }
  }

  const merged = Array.from(sectionsMap.values()).sort(
    (a, b) =>
      parseInt(a?.sibling_order ?? a?.detail?.sibling_order ?? '0', 10) -
      parseInt(b?.sibling_order ?? b?.detail?.sibling_order ?? '0', 10),
  );
  /** bulk_audit_trail: UUID blocks merged here still have procedure header on root `version_content`. */
  const rootVc =
    data.version_content &&
    typeof data.version_content === 'object' &&
    !Array.isArray(data.version_content)
      ? data.version_content
      : null;
  return { rows: merged, meta: metaFromBulkSnapshotVersionContent(rootVc) };
}

/**
 * Resolves snapshot from an API field: URL string, JSON string, inline array, or envelope object.
 * @param {unknown} snapshotRef - previous_content_snapshot or new_content_snapshot
 * @param {'original'|'new'} viewType
 * @param {{ mainEntryBulk?: boolean, previewTreeNodeId?: string, childProcedureSnapshots?: boolean }} [options]
 * @returns {Promise<{ rows: Array, meta: object|null }>}
 */
export async function resolveSnapshot(snapshotRef, viewType = 'new', options = {}) {
  if (snapshotRef == null || snapshotRef === '') {
    return { rows: [], meta: null };
  }

  if (Array.isArray(snapshotRef)) {
    return { rows: sortSectionsForSnapshotDisplay(snapshotRef), meta: null };
  }

  if (typeof snapshotRef === 'object') {
    return parseSnapshotJson(snapshotRef, viewType, options);
  }

  if (typeof snapshotRef === 'string') {
    const s = snapshotRef.trim();
    if (s.startsWith('[') || s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        return resolveSnapshot(parsed, viewType, options);
      } catch {
        return { rows: [], meta: null };
      }
    }
    const response = await fetch(s, SNAPSHOT_FETCH_INIT);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return parseSnapshotJson(data, viewType, options);
  }

  return { rows: [], meta: null };
}

/**
 * Fetches snapshot data from S3 URL and parses it into section array format.
 * Handles multiple formats: direct array, result array, originalContent/newContent, UUID-keyed merge.
 * Tries direct fetch first; if it fails (e.g. CORS), tries backend proxy when available.
 * @param {string} snapshotUrl - URL to fetch (e.g. new_content_snapshot or previous_content_snapshot)
 * @param {'original'|'new'} viewType - Which content to extract when format has both
 * @returns {Promise<Array>} Array of sections
 */
export async function fetchSnapshotContent(snapshotUrl, viewType = 'new') {
  if (!snapshotUrl) return [];

  const response = await fetch(snapshotUrl, SNAPSHOT_FETCH_INIT);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  const { rows } = parseSnapshotJson(data, viewType);
  return rows;
}

function mergeAuditVersionContentFlatForSnapshot(vc) {
  if (!vc || typeof vc !== 'object' || Array.isArray(vc)) return {};
  const inner = vc.version_content;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...vc, ...inner };
  }
  return { ...vc };
}

function pickPreviewTreeNodeIdFromAuditRow(auditData) {
  if (!auditData || typeof auditData !== 'object') return undefined;
  const fromRow = mergeAuditVersionContentFlatForSnapshot(
    auditData.version_content,
  ).preview_tree_node_id;
  if (fromRow != null && String(fromRow).trim() !== '')
    return String(fromRow).trim();
  const parentVc = auditData.parent_version_data?.version_content;
  const fromParent = mergeAuditVersionContentFlatForSnapshot(
    parentVc || {},
  ).preview_tree_node_id;
  if (fromParent != null && String(fromParent).trim() !== '')
    return String(fromParent).trim();
  return undefined;
}

/**
 * Child move/copy audits (snapshot envelope { data, operation: single_audit_trail }).
 */
function isChildNodeProcedureSnapshotAuditRow(auditData, vcMerged) {
  const hint = `${vcMerged?.action_to_show ?? ''} ${auditData?.action ?? ''}`;
  const u = hint.toUpperCase().replace(/RICH-TEXT/g, 'RICH TEXT');
  if (
    /ATTACHMENT_(?:COPIED|MOVED)|VIDEO_(?:COPIED|MOVED)|RICHTEXT_(?:COPIED|MOVED)|RICH_TEXT_(?:COPIED|MOVED)|CANVAS_(?:COPIED|MOVED)|CANVA_(?:COPIED|MOVED)|FORM_(?:COPIED|MOVED)|CHILD_NODE_(?:COPIED|MOVED)/.test(
      u,
    )
  ) {
    return true;
  }
  return /\bUSER\s+(?:COPIED|MOVED)\s+(ATTACHMENT|VIDEO|RICH\s+TEXT|RICHTEXT|CANVAS|CANVA|FORM)\b/i.test(
    hint,
  );
}

/**
 * CREATE/UPDATE/SAVE leaf procedures store full tree in new_content_snapshot with the same envelope
 * as child move/copy (`{ data: sections[], operation: 'single_audit_trail', tree_node_id, type, … }`);
 * previous_content_snapshot is often a plain section array. Human action_to_show uses "Save Canvas …".
 */
function isCreateUpdateLeafProcedureAuditRow(auditData, vcMerged) {
  const hint = `${vcMerged?.action_to_show ?? ''} ${auditData?.action ?? ''}`
    .toUpperCase()
    .replace(/RICH-TEXT/g, 'RICH_TEXT');
  return (
    /CREATE_(?:ATTACHMENT|VIDEO|CANVAS|CANVA|FORM|RICH_TEXT)/.test(hint) ||
    /UPDATE_(?:ATTACHMENT|VIDEO|CANVAS|CANVA|FORM|RICH_TEXT)/.test(hint) ||
    /SAVE_(?:ATTACHMENT|VIDEO|CANVAS|CANVA|FORM|RICH_TEXT)/.test(hint) ||
    /\bSAVE\s+(?:ATTACHMENT|VIDEO|CANVAS|CANVA|FORM|RICH_TEXT)\b/.test(hint) ||
    /\bEDIT\s+(?:ATTACHMENT|VIDEO|CANVAS|CANVA|FORM|RICH_TEXT)\b/.test(hint)
  );
}

/**
 * Step save / item-code update audits: full procedure tree in S3 `{ data: sections[], … }` (often with
 * extra root fields like `present_revisions`). Dedicated parsing avoids bulk / preview / child-envelope branches.
 */
function isAuditTrailUpdateStepProcedureTreeRow(auditData, vcMerged) {
  if (!auditData || typeof auditData !== 'object') return false;
  const vc = vcMerged && typeof vcMerged === 'object' ? vcMerged : {};
  const hint = `${vc.action_to_show ?? ''} ${auditData.action ?? ''} ${auditData.action_to_show ?? ''}`
    .toUpperCase()
    .replace(/RICH-TEXT/g, 'RICH_TEXT');
  return /\bUPDATE_STEP\b/.test(hint);
}

/**
 * CREATE/UPDATE/SAVE leaf nodes with full procedure tree (`UPDATE_CANVAS`, …) — same envelope as step saves.
 */
function isAuditTrailLeafEditProcedureTreeRow(auditData, vcMerged) {
  if (!auditData || typeof auditData !== 'object') return false;
  const vc = vcMerged && typeof vcMerged === 'object' ? vcMerged : {};
  const hint = `${vc.action_to_show ?? ''} ${auditData.action ?? ''} ${auditData.action_to_show ?? ''}`
    .toUpperCase()
    .replace(/RICH-TEXT/g, 'RICH_TEXT');
  if (/\bUPDATE_STEP\b/.test(hint)) return false;
  return (
    /\b(?:UPDATE|SAVE|CREATE)_(?:CANVAS|CANVA)\b/.test(hint) ||
    /\b(?:UPDATE|SAVE|CREATE)_(?:VIDEO|ATTACHMENT)\b/.test(hint) ||
    /\b(?:UPDATE|SAVE|CREATE)_(?:RICH_TEXT|RICHTEXT|FORM)\b/.test(hint) ||
    /\b(?:SAVE|EDIT)\s+CANVAS\b/.test(hint) ||
    /\b(?:SAVE|EDIT)\s+VIDEO\b/.test(hint) ||
    /\b(?:SAVE|EDIT)\s+ATTACHMENT\b/.test(hint) ||
    /\b(?:SAVE|EDIT)\s+RICH\s+TEXT\b/.test(hint)
  );
}

/**
 * Copy & replace audits where audit `version_content` has action REVISION_COPIED and main_entry — View Original /
 * View New come from bulk S3 snapshot {@link tryMainEntryBulkSnapshot} (first_entry.originalContent + last UUID newContent).
 */
export function isAuditTrailRevisionCopiedMainEntryVc(vcMerged) {
  if (!vcMerged || typeof vcMerged !== "object") return false;
  const ats = String(vcMerged.action_to_show ?? "").trim().toUpperCase();
  if (ats !== "REVISION_COPIED") return false;
  return isTruthyMainEntrySnapshot(vcMerged.main_entry);
}

/**
 * Copy & replace audits only — enables COND1–3 in snapshot parsing (`parseSnapshotJson`).
 */
export function isAuditTrailCopyReplaceRow(auditData, vcMerged) {
  if (!auditData || typeof auditData !== 'object') return false;
  const vc = vcMerged && typeof vcMerged === 'object' ? vcMerged : {};
  const hint = `${vc.action_to_show ?? ''} ${auditData.action ?? ''} ${auditData.action_to_show ?? ''}`.toUpperCase();
  const url = String(auditData.new_content_snapshot ?? '');
  return (
    /COPIED\s+AND\s+REPLACED|COPY\s+AND\s+REPLACE/i.test(hint) ||
    /COPEID_REPLACE/i.test(url) ||
    /COPEID_REPLACE/i.test(hint)
  );
}

/**
 * Options for {@link resolveSnapshot} aligned with ReviewLayout / RevisionPreview.
 */
export function getSnapshotParseOptionsForAuditRow(auditData) {
  if (!auditData || typeof auditData !== 'object') {
    return {
      mainEntryBulk: false,
      previewTreeNodeId: undefined,
      childProcedureSnapshots: false,
      copyReplaceBulk: false,
      auditFirstEntryFlag: false,
      auditVcFirstEntryFlag: false,
      revisionCopiedMainEntryBulk: false,
      auditUpdateStepProcedureTree: false,
      auditLeafEditProcedureTree: false,
      preserveSnapshotSectionOrder: false,
    };
  }
  const vcMerged = mergeAuditVersionContentFlatForSnapshot(
    auditData.version_content,
  );
  const auditUpdateStepProcedureTree =
    isAuditTrailUpdateStepProcedureTreeRow(auditData, vcMerged);
  const auditLeafEditProcedureTree =
    isAuditTrailLeafEditProcedureTreeRow(auditData, vcMerged);
  const preserveSnapshotSectionOrder =
    auditUpdateStepProcedureTree || auditLeafEditProcedureTree;
  const mainEntryBulk = isTruthyMainEntrySnapshot(vcMerged.main_entry);
  const previewTid = pickPreviewTreeNodeIdFromAuditRow(auditData);
  let childProcedureSnapshots =
    isChildNodeProcedureSnapshotAuditRow(auditData, vcMerged) ||
    isCreateUpdateLeafProcedureAuditRow(auditData, vcMerged);
  const copyReplaceBulk = isAuditTrailCopyReplaceRow(auditData, vcMerged);
  const auditVcFirstEntryFlag = pickAuditFirstEntryFlagFromVc(vcMerged);
  const auditFirstEntryFlag = copyReplaceBulk && auditVcFirstEntryFlag;
  const revisionCopiedMainEntryBulk =
    isAuditTrailRevisionCopiedMainEntryVc(vcMerged);

  /**
   * Copy & replace (bulk_audit_trail, UUID-keyed blocks in one S3 file — e.g. *_COPEID_REPLACE_*.json):
   * a leaf row (canvas, attachment, video, rich text) still matches {@link isChildNodeProcedureSnapshotAuditRow}
   * ("User Copied … Canvas …"). That path clears previewTreeNodeId and parses `{ data, single_audit_trail }`,
   * which is wrong for bulk snapshots. When audit has preview_tree_node_id and neither main_entry nor
   * version_content.first_entry, resolve {@link tryPreviewTreeBulkSnapshot} instead.
   */
  const newSnapshotUrl = String(auditData.new_content_snapshot ?? "");
  const looksLikeBulkCopyReplaceSnapshotFile =
    copyReplaceBulk || /COPEID_REPLACE/i.test(newSnapshotUrl);
  if (
    previewTid &&
    !mainEntryBulk &&
    !auditVcFirstEntryFlag &&
    childProcedureSnapshots &&
    looksLikeBulkCopyReplaceSnapshotFile
  ) {
    childProcedureSnapshots = false;
  }

  return {
    mainEntryBulk,
    previewTreeNodeId: childProcedureSnapshots ? undefined : previewTid,
    childProcedureSnapshots,
    copyReplaceBulk,
    auditFirstEntryFlag,
    auditVcFirstEntryFlag,
    revisionCopiedMainEntryBulk,
    auditUpdateStepProcedureTree,
    auditLeafEditProcedureTree,
    preserveSnapshotSectionOrder,
  };
}
